/**
 * @fileoverview OAuth callback handler component for processing authentication redirects.
 * Handles successful logins, errors, and session verification after OAuth flow.
 *
 * @module components/auth/AuthCallback
 */

import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../services/supabase'
import { ROUTES } from '../../constants/routes'
import { isUserInitiatedLogin } from '../../utils/loginIntent'
import clientTokenService from '../../services/clientTokenService'

/**
 * AuthCallback component - Processes OAuth authentication callbacks.
 * Handles the redirect from OAuth providers (Google, Facebook) after authentication.
 * Verifies the session and redirects users to the home page on success.
 *
 * @component
 * @returns {JSX.Element} Loading or error message during callback processing
 *
 * @description
 * OAuth Flow:
 * 1. User clicks OAuth login button
 * 2. Redirected to provider (Google/Facebook)
 * 3. After auth, provider redirects to /auth/callback
 * 4. This component processes the callback
 * 5. Verifies session with Supabase
 * 6. Redirects to home on success or login on failure
 *
 * Error Handling:
 * - Checks URL hash for error parameters
 * - Validates session with Supabase
 * - Shows error message and auto-redirects to login
 * - Uses ref to prevent double processing
 *
 * @example
 * // Route configuration
 * <Route path="/auth/callback" element={<AuthCallback />} />
 */
export const AuthCallback = () => {
  const navigate = useNavigate()
  const [error, setError] = useState(null)
  const hasProcessed = useRef(false)

  useEffect(() => {
    // Only process the callback once
    if (hasProcessed.current) return
    hasProcessed.current = true

    const handleCallback = async () => {
      try {
        // Check if we have an error in the URL (OAuth failed)
        const hashParams = new URLSearchParams(window.location.hash.substring(1))
        const errorCode = hashParams.get('error')
        const errorDescription = hashParams.get('error_description')

        if (errorCode) {
          setError(errorDescription || 'Authentication failed')
          setTimeout(() => navigate(ROUTES.LOGIN), 3000)
          return
        }

        // Get the current session to check if OAuth was successful
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession()

        if (sessionError) {
          console.error('Session error:', sessionError)
          setError('Failed to complete authentication')
          setTimeout(() => navigate(ROUTES.LOGIN), 3000)
          return
        }

        if (session?.user) {
          const user = session.user

          // Check if this is a user-initiated login and the user is non-guest
          const isLoginFlow = isUserInitiatedLogin()
          const userLevel = user.app_metadata?.user_level || 'guest'

          // Dispatch conversation resume event for eligible users
          if (isLoginFlow && userLevel !== 'guest') {
            const conversationHistory = clientTokenService.getConversationHistory()

            if (conversationHistory && conversationHistory.length > 0) {
              // Get existing conversation data
              const conversationId =
                localStorage.getItem('et_ai_conversation_id') ||
                clientTokenService.getCurrentConversationId()
              const startTime = localStorage.getItem('et_ai_conversation_start_time') || Date.now()

              // Dispatch the conversation-resumed event
              const resumeEvent = new CustomEvent('conversation-resumed', {
                detail: {
                  data: {
                    id: conversationId,
                    messages: conversationHistory,
                    startTime: parseInt(startTime, 10),
                  },
                  timestamp: Date.now(),
                  operation: 'resume_conversation',
                  source: 'oauth',
                  userLevel,
                  migrationId: clientTokenService.getAnonymousId(), // For analytics tracking
                },
              })

              window.dispatchEvent(resumeEvent)
            }
          }

          // Navigate to home page after successful authentication
          navigate(ROUTES.HOME, { replace: true })
        } else {
          // No session found, redirect to login
          navigate(ROUTES.LOGIN, { replace: true })
        }
      } catch (err) {
        console.error('Auth callback error:', err)
        setError('An error occurred during authentication')
        setTimeout(() => navigate('/login'), 3000)
      }
    }

    handleCallback()
  }, [navigate])

  return (
    <div className="min-h-screen flex items-center justify-center bg-dark-primary">
      <div className="max-w-md w-full space-y-8 p-8">
        <div className="text-center">
          {error ? (
            <>
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-900/20 border border-red-500/20">
                <svg
                  className="h-6 w-6 text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h2 className="mt-6 text-2xl font-bold text-white">Authentication Failed</h2>
              <p className="mt-2 text-sm text-gray-400">{error}</p>
              <p className="mt-4 text-sm text-gray-500">Redirecting to login...</p>
            </>
          ) : (
            <>
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-dark-secondary border border-dark-border">
                <svg className="h-6 w-6 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
              <h2 className="mt-6 text-2xl font-bold text-white">Completing sign in...</h2>
              <p className="mt-2 text-sm text-gray-400">Please wait while we redirect you.</p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
